import React from "react";
import { Link } from "react-router-dom";
import { Heart, HomeIcon, Sparkles, PhoneCall, ArrowRight, CheckCircle2, Shield, Users, MessageCircle } from "lucide-react";
import { SERVICES } from "../data/services";
import { VIDEOS } from "../data/videos";
import { IMG } from "../data/images";
import VideoSection from "../components/VideoSection";
import { trackPhoneClick } from "../lib/tracking";

const STEPS = [
  { n: "01", title: "Say hello", text: "Call or submit our form — plain English, no jargon." },
  { n: "02", title: "We listen", text: "Goals, preferences, what's not working today." },
  { n: "03", title: "We match", text: "The right team and (if needed) a home." },
  { n: "04", title: "You move forward", text: "Support starts. Regular reviews keep it honest." },
];

const REASSURE = [
  { Icon: HomeIcon, title: "Real homes", text: "Warm, well-located, thoughtfully designed." },
  { Icon: Heart, title: "Right team", text: "Matched on values + consistent rostering." },
  { Icon: Sparkles, title: "Honest support", text: "Goal-focused plans and clear communication." },
  { Icon: Shield, title: "Safe by design", text: "Screened, trained, trauma-informed workforce." },
];

export default function ForParticipantsPage() {
  return (
    <div data-testid="page-for-participants">
      {/* Hero */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[320px] h-[320px] -left-16 top-10 opacity-75 hidden md:block" aria-hidden />
        <div className="alpha-orb alpha-orb-green w-[280px] h-[280px] right-[-40px] top-24 opacity-60 hidden md:block" aria-hidden />
        <div className="alpha-container pt-10 pb-12 md:pt-16 md:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center relative">
          <div className="lg:col-span-7 alpha-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white/85 backdrop-blur text-xs font-semibold text-[color:var(--alpha-purple-800)]">
              <Users className="w-3.5 h-3.5" /> For participants &amp; families
            </div>
            <h1 className="mt-5 text-[36px] leading-[1.05] sm:text-5xl lg:text-[60px] font-display font-medium tracking-tight">
              Support that <span className="alpha-serif-italic alpha-gradient-text">sits alongside you</span>.
            </h1>
            <p className="mt-4 text-[15.5px] md:text-lg text-[color:var(--alpha-mute)] max-w-xl">
              NDIS supports built around real goals — so your plan actually works in everyday life.
            </p>
            <div className="mt-6 md:mt-7 flex flex-wrap gap-2.5 md:gap-3">
              <Link to="/referrals/participant" className="alpha-btn-primary flex-1 sm:flex-none justify-center" data-testid="fp-cta-refer">
                Start your referral <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "for-participants-hero")} className="alpha-btn-green flex-1 sm:flex-none justify-center">
                <PhoneCall className="w-4 h-4" /> 1300 380 692
              </a>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-[color:var(--alpha-mute)]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Family-friendly intake</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Named coordinator</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[color:var(--alpha-green-600)]" /> Reviewed quarterly</span>
            </div>
          </div>
          <div className="lg:col-span-5 alpha-fade-up alpha-delay-2">
            <div className="relative rounded-[24px] md:rounded-[28px] overflow-hidden aspect-[4/5] max-h-[500px] shadow-2xl alpha-ring-gradient">
              <img
                src={IMG.forParticipantsHero}
                alt="Premium Alpha master bedroom with timber-slat wardrobe and direct ensuite access — calm, modern living"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14322F]/55 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 alpha-glass rounded-2xl p-3.5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[color:var(--alpha-green-600)] text-white flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--alpha-mute)]">We reply in</div>
                  <div className="font-display font-semibold text-[14px] !text-[color:var(--alpha-ink)]">1 business day</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Workers & Homes — explainer video */}
      <VideoSection
        src={VIDEOS["support-worker"].url}
        eyebrow="Watch · Support workers & homes"
        title="The team behind every shift."
        subtitle={VIDEOS["support-worker"].blurb}
        testid="fp-video-support-worker"
      />

      {/* Understand supports — visual grid */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-6 md:mb-8 alpha-reveal">
            <div>
              <div className="alpha-overline">Understand your supports</div>
              <h2 className="mt-2 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">Every support, in plain English.</h2>
            </div>
            <Link to="/services" className="alpha-btn-ghost text-sm !py-2.5 !px-4">All services <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = s.Icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="alpha-card p-5 md:p-6 group flex gap-4 hover:-translate-y-1"
                  data-testid={`fp-svc-${s.slug}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center shrink-0 group-hover:bg-[color:var(--alpha-purple-600)] group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-display text-[15px] md:text-base font-semibold leading-snug group-hover:text-[color:var(--alpha-purple-700)]">{s.short}</h3>
                    <p className="text-xs md:text-sm text-[color:var(--alpha-mute)] mt-1 line-clamp-2">{s.intro}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works — timeline */}
      <section className="alpha-section-tight alpha-gradient-soft-band">
        <div className="alpha-container">
          <div className="alpha-overline">How it works</div>
          <h2 className="mt-2 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">Four calm steps, nothing scary.</h2>
          <ol className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {STEPS.map((s, i) => (
              <li key={s.n} className="alpha-card p-5 md:p-6 relative overflow-hidden hover:-translate-y-1">
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full alpha-gradient opacity-10 blur-xl" aria-hidden />
                <div className="font-display text-[28px] font-semibold alpha-gradient-text">{s.n}</div>
                <div className="mt-1.5 font-display text-base md:text-lg font-semibold">{s.title}</div>
                <p className="text-[13px] md:text-sm text-[color:var(--alpha-mute)] mt-1">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Reassurance */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {REASSURE.map((c) => (
              <div key={c.title} className="alpha-card p-6 hover:-translate-y-1" data-testid={`fp-reassure-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-11 h-11 rounded-lg alpha-gradient text-white flex items-center justify-center">
                  <c.Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vacancies teaser */}
      <section className="alpha-section-tight">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[24px] md:rounded-[32px] alpha-gradient p-8 md:p-12 text-white alpha-noise">
            <div className="alpha-orb alpha-orb-green w-[260px] h-[260px] right-[-40px] top-[-40px] opacity-60 hidden md:block" aria-hidden />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-semibold uppercase tracking-[0.22em]">
                  <HomeIcon className="w-3.5 h-3.5" /> Our homes
                </div>
                <h2 className="mt-4 font-display text-3xl md:text-[40px] font-medium leading-[1.05] !text-white">
                  Browse current <span className="alpha-serif-italic">vacancies</span>.
                </h2>
                <p className="mt-3 text-white/85 text-[15px]">SIL, SDA, STA &amp; MTA homes across Melbourne &amp; Perth.</p>
              </div>
              <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-2.5">
                <Link to="/vacancies" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-purple-50)] transition inline-flex items-center justify-center gap-2">
                  View vacancies <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/referrals/participant" className="border-2 border-white/50 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2">
                  Start a referral
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
